import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { fDate } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { ButtonsLike } from 'src/components/custom-buttons';
import { ShowMoreLessText } from 'src/components/text-utils';
import { CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export function ReviewItem({ id, uid, review, reviewHook }) {
  const { t } = useTranslate();

  const menuActions = usePopover();

  const { removeReview, reportReview } = reviewHook;

  const renderInfo = () => (
    <Stack
      alignItems="center"
      direction={{ xs: 'row', md: 'column' }}
      sx={{ gap: { xs: 2, md: 1 }, width: { md: 240 }, textAlign: { md: 'center' } }}
    >
      <Avatar src={review.photoURL} alt={review.displayName} sx={{ width: 48, height: 48 }} />

      <ListItemText
        primary={
          <Link
            noWrap
            color="inherit"
            component={RouterLink}
            href={`${paths.user.profile(review.authorUID)}${newSearchParams({ country: review.country })}`}
          >
            {review.displayName}
          </Link>
        }
        secondary={review.postedAt ? fDate(review.postedAt) : `${t('Publishing')}...`}
        slotProps={{
          primary: { noWrap: true, variant: 'body2', mb: 0.5 },
          secondary: { noWrap: true, variant: 'caption', component: 'span' },
        }}
      />
    </Stack>
  );

  const renderContent = () => (
    <Stack spacing={1} flexGrow={1} justifyContent="center">
      <Rating size="small" value={review.rating} precision={0.1} readOnly />

      {review.isPurchased && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ color: 'success.main', typography: 'caption' }}
        >
          <Iconify icon="ic:round-verified" width={16} sx={{ mr: 0.5 }} />
          {t('Verified purchase')}
        </Stack>
      )}

      <ShowMoreLessText text={review.comment} typography="body2" />

      {!!review.attachments?.length && (
        <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ pt: 1 }}>
          {review.attachments.map((attachment) => (
            <Box
              key={attachment}
              component="img"
              alt={attachment}
              src={attachment}
              sx={{ width: 64, height: 64, borderRadius: 1.5 }}
            />
          ))}
        </Stack>
      )}

      <ButtonsLike
        uid={uid}
        totalLikes={review.totalLikes}
        totalDislikes={review.totalDislikes}
        path={`ratings/${id}/reviews/${review.id}`}
      />
    </Stack>
  );

  const handleActions = async (type) => {
    menuActions.onClose();
    if (type === 'delete') removeReview(review);
    if (type === 'report') reportReview(review);
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      onClose={menuActions.onClose}
      anchorEl={menuActions.anchorEl}
    >
      <MenuList>
        {uid === review.authorUID && (
          <MenuItem onClick={() => handleActions('delete')} sx={{ color: 'error.main' }}>
            <Iconify icon="solar:trash-bin-trash-bold" />
            {t('Delete')}
          </MenuItem>
        )}

        {uid !== review.authorUID && (
          <MenuItem onClick={() => handleActions('report')}>
            <Iconify icon="solar:danger-bold" sx={{ color: 'error.main' }} />
            {t('Report rating')}
          </MenuItem>
        )}
      </MenuList>
    </CustomPopover>
  );

  const renderMenuButton = () => (
    <IconButton onClick={menuActions.onOpen} sx={{ position: 'absolute', top: 16, right: 16 }}>
      <Iconify icon="eva:more-vertical-fill" />
    </IconButton>
  );

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', md: 'row' }}
      sx={[
        (theme) => ({
          position: 'relative',
          py: { xs: 3, md: 2.5 },
          px: { xs: 2.5, md: 0 },
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
        }),
      ]}
    >
      {renderInfo()}
      {renderContent()}
      {uid && renderMenuButton()}
      {uid && renderMenuActions()}
    </Stack>
  );
}
